import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { BookerFlightWithControls } from '@shared/booker/types/booker-flight.type';
// todo: in this file we have a few 'as any' type coercions to work around only
//  some typing in the form - remove these when you type this entire section correctly
import {
  prePopCityPairMultiCity,
  prePopDepartureDatesMultiCity,
} from '@shared/booker/utils/booker-utils';
import { isInternational } from '@shared/booker/utils/warnings-check';
import { RecentAirSearch } from '@shared/types';
import { getControlVal } from '@shared/ui/utils/form-control-utils/get-control-val';
import { isBefore, isValid, startOfToday } from 'date-fns';
import { equals } from 'ramda';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { newMultiCityFormRow } from '../utils/utils';

@Injectable()
export class BookerMultiCityStore {
  minFlights = 2;
  maxFlights = 4;
  flights: FormArray<FormGroup<BookerFlightWithControls>> = new FormArray<
    FormGroup<BookerFlightWithControls>
  >([newMultiCityFormRow(), newMultiCityFormRow()]);

  public flights$: BehaviorSubject<
    FormArray<FormGroup<BookerFlightWithControls>>
  > = new BehaviorSubject<FormArray<FormGroup<BookerFlightWithControls>>>(
    this.flights,
  );
  public flight$: (
    index: number,
  ) => Observable<FormGroup<BookerFlightWithControls>>;
  public isMaxFlights$: Observable<boolean>;
  public minDates$: Observable<Date[]>;
  public minDatesBehaviourSubject$: BehaviorSubject<Date[]>;
  public departureDates$: Observable<Date[]>;
  public hasInterlineFlight$: Observable<boolean>;
  public hasInternationalFlight$: Observable<boolean>;
  public minDate$: (index: number) => Observable<Date>;

  constructor() {
    this.flight$ = (index: number) =>
      this.flights$.pipe(map(flights => flights.controls[index]));

    this.isMaxFlights$ = this.flights$.pipe(
      map(flights => {
        return flights.controls.length >= this.maxFlights;
      }),
    );

    this.departureDates$ = getControlVal(this.flights as any).pipe(
      map(values => {
        return values?.map(group => group?.departureDate);
      }),
      distinctUntilChanged<Date[]>(equals),
    );

    this.minDates$ = this.departureDates$.pipe(
      map(dates => {
        const minDates: Date[] = [];
        for (let i = 0; i < dates.length; i++) {
          let minDate = i ? this.getMinDateForRow(dates, i) : startOfToday();
          if (isBefore(dates[i], minDate)) {
            (this.flights as any).get([i, 'departureDate']).reset();
            dates[i] = null;
            minDate = this.getMinDateForRow(dates, i);
          }
          minDates.push(minDate);
        }
        return minDates;
      }),
      distinctUntilChanged<Date[]>(equals),
    );

    this.minDatesBehaviourSubject$ = new BehaviorSubject([]);
    this.minDates$.subscribe(this.minDatesBehaviourSubject$);
    this.minDate$ = (index: number): Observable<Date> =>
      this.minDatesBehaviourSubject$.pipe(map(dates => dates[index]));

    this.hasInternationalFlight$ = getControlVal(this.flights as any).pipe(
      map(
        flights =>
          flights?.some(
            flight =>
              flight.fromAirport &&
              flight.toAirport &&
              isInternational(flight.fromAirport, flight.toAirport),
          ),
      ),
    );

    this.hasInterlineFlight$ = getControlVal(this.flights as any).pipe(
      map(flights =>
        flights.some(
          ({ fromAirport, toAirport }) =>
            !!fromAirport?.interline || !!toAirport?.interline,
        ),
      ),
    );
  }

  readonly addFlight = () => {
    if (this.flights.length < this.maxFlights) {
      this.flights.push(newMultiCityFormRow());
      this.flights$.next(this.flights);
    }
  };

  readonly removeFlight = (index: number) => {
    if (this.flights.length > this.minFlights) {
      this.flights.removeAt(index);
      this.flights$.next(this.flights);
    }
  };

  public setPrePopulatedSearch = (
    recentAirSearches: RecentAirSearch[],
    airportHashMap = {},
  ) => {
    const recentAirSearchesLength =
      recentAirSearches[0].originCode.length <= this.maxFlights
        ? recentAirSearches[0].originCode.length
        : this.maxFlights;

    for (let index = 0; index < recentAirSearchesLength; index++) {
      if (index > this.flights.controls.length - 1) {
        this.addFlight();
      }
      const flight = this.flights.controls[index];

      prePopDepartureDatesMultiCity(
        recentAirSearches[0],
        flight.get('departureDate') as any,
        index,
      );

      prePopCityPairMultiCity(
        airportHashMap,
        recentAirSearches[0],
        flight.get('fromAirport') as any,
        flight.get('toAirport') as any,
        index,
      );
    }
  };

  private getMinDateForRow = (dates: Date[], index: number): Date => {
    if (dates.length && index) {
      for (let i = index - 1; i >= 0; i--) {
        if (isValid(dates[i])) {
          return dates[i];
        }
      }
    }
    return startOfToday();
  };
}
